import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Card, CardContent, CardFooter, Input, Select, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CustomerDetail, Priority, Ref } from '@/lib/types';
import { useTicketMeta } from './tickets-page';

export function NewTicketPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can } = useAuth();
  const { data: meta } = useTicketMeta();
  const [areaId, setAreaId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('NORMAL');
  const [customer, setCustomer] = useState<Ref | null>(
    params.get('customerId') ? { id: params.get('customerId')!, name: params.get('customerName') ?? 'Kunde' } : null,
  );
  const [contactId, setContactId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [teamId, setTeamId] = useState('');

  const { data: customerDetail } = useQuery({
    queryKey: ['customers', customer?.id],
    queryFn: () => api<CustomerDetail>(`/customers/${customer!.id}`),
    enabled: Boolean(customer),
  });

  const effectiveArea = areaId || meta?.areas.find((a) => a.active)?.id || '';
  const categories = meta?.categories.filter((c) => !c.areaId || c.areaId === effectiveArea) ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      api<{ id: string; number: string }>('/tickets', {
        method: 'POST',
        body: {
          areaId: effectiveArea,
          categoryId: categoryId || null,
          subject,
          description,
          priority,
          customerId: customer?.id ?? null,
          contactId: contactId || null,
          assigneeId: assigneeId || null,
          teamId: teamId || null,
        },
      }),
    onSuccess: (t) => {
      toast.success(`Ticket ${t.number} angelegt`);
      navigate(`/tickets/${t.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const valid = effectiveArea && subject.trim().length >= 3 && description.trim().length > 0;

  return (
    <>
      <PageHeader title="Neues Ticket" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) mutation.mutate();
        }}
      >
        <Card className="max-w-4xl">
          <CardContent className="grid gap-4 pt-5 sm:grid-cols-2">
            <Field label="Bereich" htmlFor="n-area">
              <Select id="n-area" value={effectiveArea} onChange={(e) => { setAreaId(e.target.value); setCategoryId(''); }}>
                {meta?.areas.filter((a) => a.active).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Kategorie" htmlFor="n-cat">
              <Select id="n-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">– keine –</option>
                {categories.filter((c) => !c.parentId).map((c) => [
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>,
                  ...categories.filter((s) => s.parentId === c.id).map((s) => (
                    <option key={s.id} value={s.id}>
                      {'  '}↳ {s.name}
                    </option>
                  )),
                ])}
              </Select>
            </Field>
            <Field label="Betreff" htmlFor="n-subject" className="sm:col-span-2">
              <Input id="n-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} autoFocus />
            </Field>
            <Field label="Beschreibung" htmlFor="n-desc" className="sm:col-span-2">
              <Textarea id="n-desc" rows={7} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Kunde" htmlFor="n-customer">
              <CustomerPicker id="n-customer" value={customer} onChange={(c) => { setCustomer(c); setContactId(''); }} />
            </Field>
            <Field label="Ansprechpartner" htmlFor="n-contact">
              <Select id="n-contact" value={contactId} onChange={(e) => setContactId(e.target.value)} disabled={!customerDetail?.contacts.length}>
                <option value="">– keiner –</option>
                {customerDetail?.contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priorität" htmlFor="n-prio">
              <Select id="n-prio" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                <option value="LOW">Niedrig</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">Hoch</option>
                <option value="URGENT">Dringend</option>
              </Select>
            </Field>
            {can('tickets.assign') && (
              <>
                <Field label="Bearbeiter" htmlFor="n-assignee">
                  <Select id="n-assignee" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                    <option value="">– nicht zugewiesen –</option>
                    {meta?.agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Team" htmlFor="n-team">
                  <Select id="n-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                    <option value="">– kein Team –</option>
                    {meta?.teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </>
            )}
          </CardContent>
          <CardFooter>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!valid} loading={mutation.isPending}>
              Ticket anlegen
            </Button>
          </CardFooter>
        </Card>
      </form>
    </>
  );
}
